import { Box, Chip, useTheme } from "@mui/material";
import { useMemo } from "react";
import { BasketStatus } from "./utils";

export type BasketStatusChipProps =
  | { status: BasketStatus.Filled }
  | {
      status: BasketStatus.Assigned;
      assignedName: string;
      assignedUid: string;
    }
  | { status: BasketStatus.Unassigned };

interface BasketStatusStyles {
  iconColor: string;
  backgroundColor: string;
  border: string;
  color: string;
  boxShadow: string;
  label: string;
  height: string;
  fontSize: string;
  fontWeight: string;
}

const useBasketStatusStyles = (
  props: BasketStatusChipProps
): BasketStatusStyles => {
  const theme = useTheme();

  return useMemo(() => {
    const baseStyles = {
      height: "22px",
      fontSize: "0.7rem",
      fontWeight: "medium",
      boxShadow: "1px 1px 3px rgba(0,0,0,0.2)",
      width: "fit-content",
    };

    switch (props.status) {
      case BasketStatus.Unassigned:
        return {
          ...baseStyles,
          iconColor: theme.palette.grey[400],
          backgroundColor: theme.palette.grey[100],
          border: `1px solid ${theme.palette.grey[300]}`,
          color: theme.palette.text.secondary,
          boxShadow: "inset 2px 2px 3px rgba(0,0,0,0.2)",
          label: "Unassigned",
        };
      case BasketStatus.Filled:
        return {
          ...baseStyles,
          iconColor: theme.palette.success.main,
          backgroundColor: theme.palette.success.light + "15",
          border: `1px solid ${theme.palette.success.light}`,
          color: theme.palette.text.primary,
          label: "Filled",
        };
      case BasketStatus.Assigned:
        return {
          ...baseStyles,
          iconColor: theme.palette.primary.main,
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          color: theme.palette.text.primary,
          label: props.assignedName,
        };
    }
  }, [props, theme]);
};

/**
 * BasketStatusChip is a component that displays a status chip for a load basket.
 * It is used to indicate the status of a load basket.
 * e.g. filled, assigned, or unassigned.
 */
export const BasketStatusChip = (props: BasketStatusChipProps) => {
  const styles = useBasketStatusStyles(props);

  return (
    <Chip
      size="small"
      label={styles.label}
      icon={
        <Box
          sx={{
            backgroundColor: styles.iconColor,
            padding: 0.5,
            marginLeft: 0.5,
            borderRadius: "50%",
          }}
        />
      }
      sx={{ ...styles }}
    />
  );
};
